import { useRef } from 'react';

const MAX_CONSECUTIVE_FAILURES = 3;
export const OTP_SEND_COOLDOWN_MS = 30000;

/**
 * Tracks consecutive send-OTP failures for the current page visit and
 * imposes a short cooldown after too many in a row, so a frustrated user
 * (or an impatient repeat-click) can't keep hammering a backend that's
 * already rejecting them — backend logs showed a handful of numbers
 * retrying rapidly (up to 9 attempts in 35 seconds), tripping the
 * carrier's own per-number send-rate-limit and surfacing as a scary
 * generic "General Server Error" instead of a clear cooldown message.
 */
export default function useOtpSendThrottle() {
  const failuresRef = useRef(0);
  const cooldownUntilRef = useRef(0);

  const isOnCooldown = () => Date.now() < cooldownUntilRef.current;

  const cooldownSecondsLeft = () =>
    Math.max(1, Math.ceil((cooldownUntilRef.current - Date.now()) / 1000));

  const recordSuccess = () => {
    failuresRef.current = 0;
  };

  // Returns true when this failure just triggered a new cooldown.
  const recordFailure = () => {
    failuresRef.current += 1;
    if (failuresRef.current >= MAX_CONSECUTIVE_FAILURES) {
      cooldownUntilRef.current = Date.now() + OTP_SEND_COOLDOWN_MS;
      failuresRef.current = 0;
      return true;
    }
    return false;
  };

  return { isOnCooldown, cooldownSecondsLeft, recordSuccess, recordFailure };
}
